import {
  smartChargingLightOrder,
  smartChargingOnboarding,
} from "src/api/smartChargingClients";
import { useForm } from "react-hook-form";
import { Box, Stack } from "@mui/material";
import { ROUTES } from "src/consts/routes";
import { MSG_IDS } from "src/consts/msgIds";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Text from "src/components/Displays/Text";
import { yupResolver } from "@hookform/resolvers/yup";
import SubmitButton from "src/components/Shared/SubmitButton";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { SMART_CHARGING_ROUTES } from "src/consts/Paths/SmartCharging";
import { SESSION_KEYS, SMART_CHARGING_WALLBOX_DATA } from "src/consts/session";
import TermsInputsForm from "src/components/Forms/SmartCharging/TermsInputsForm";
import { termsInputsFormSchema } from "src/schema/SmartCharging/termsInputsFormSchema";

export interface TermsFormValues {
  agb: boolean;
}

const Terms = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formSubmitDisabled, setFormSubmitDisabled] = useState(true);
  const [getTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);

  const [getCustomerAccess] = useSessionStorage<string>(
    SESSION_KEYS.MFE_CUSTOMER_ACCESS,
  );
  const [getSessionStorageItem] =
    useSessionStorage<SMART_CHARGING_WALLBOX_DATA>(
      SESSION_KEYS.SMART_CHARGING_WALLBOX_DATA,
    );
  const [, setRedirectUrl] = useSessionStorage<string>(
    SESSION_KEYS.REDIRECT_URL,
  );
  const [isCompCheckFromSell] = useSessionStorage<string>(
    SESSION_KEYS.IS_COMP_CHECK_FROM_SELL,
  );

  const manufacturer = getSessionStorageItem()?.manufacturer ?? "";
  const modelName = getSessionStorageItem()?.modelName ?? "";

  const form = useForm<TermsFormValues>({
    defaultValues: {
      agb: false,
    },
    resolver: yupResolver(termsInputsFormSchema),
  });

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (name === "agb") {
        if (form.getValues().agb === true) {
          setFormSubmitDisabled(false);
        } else {
          setFormSubmitDisabled(true);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (data: TermsFormValues) => {
    setIsLoading(true);
    const transferId = getTransferId();
    if (!transferId) return;

    const payload = {
      manufacturer,
      modelName,
      compatibilityStatus: getSessionStorageItem()?.compatibilityStatus || "",
      tosAccepted: data.agb,
    };

    try {
      const customerAccess = getCustomerAccess();

      if (!customerAccess) {
        const isCompCheckFromSellValue = isCompCheckFromSell();
        if (isCompCheckFromSellValue && isCompCheckFromSellValue === "true") {
          const response = await smartChargingLightOrder({
            payload,
            transferId,
          });
          setRedirectUrl(response.redirectUrl);
          setTimeout(() => {
            setIsLoading(false);
          }, 1000);
          navigate(SMART_CHARGING_ROUTES.COMPATIBILITY_SUCCESS);
        }
        return;
      }

      await smartChargingOnboarding({
        payload,
        xIsonCustomerToken: customerAccess,
      });
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      navigate(SMART_CHARGING_ROUTES.COMPATIBILITY_SUCCESS);
    } catch (e) {
      navigate(
        `${ROUTES.ERROR}?msgId=${MSG_IDS.smart_charging_terms_accept_error}`,
      );
    }
  };

  return (
    <>
      <Stack gap={5}>
        <Text
          text={t("page.wallboxTerms.pageTitle")}
          sx={(theme) => ({
            fontWeight: 500,
            fontSize: "32px",
            lineHeight: "40px",
            fontFamily: "officina",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Stack direction={"row"} gap={1}>
          <Text
            text={t("page.wallboxTerms.pageSubTitle1")}
            sx={(theme) => ({
              fontSize: "14px",
              lineHeight: "24px",
              fontFamily: "Inter",
              fontWeight: "bold",
              color: theme.palette.mainFont.primary,
            })}
          />
          <Text
            text={`${manufacturer} - ${modelName}`}
            sx={(theme) => ({
              fontSize: "14px",
              lineHeight: "24px",
              fontFamily: "Inter",
              color: theme.palette.mainFont.primary,
            })}
          />
        </Stack>

        <Text
          text={t("page.wallboxTerms.pageSubTitle2")}
          sx={(theme) => ({
            fontSize: "14px",
            lineHeight: "24px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Stack
          gap={5}
          component={"form"}
          autoComplete="off"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <TermsInputsForm form={form} />
          <Box>
            <SubmitButton
              type="submit"
              isLoading={isLoading}
              disabled={formSubmitDisabled}
              buttonText={t("page.wallboxTerms.formFields.submitButton")}
              sx={(theme) => ({
                fontSize: "16px",
                textTransform: "none",
                color: theme.palette.mainFont.primary,
                width: { xs: "100%", sm: "fit-content" },
                "&:disabled": {
                  fontSize: "16px",
                  cursor: "not-allowed",
                  pointerEvents: "all !important",
                  bgcolor: "rgba(0, 0, 0, 0.11)",
                  color: isLoading ? undefined : theme.palette.grey[300],
                },
              })}
            />
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

export default Terms;
